// The cabinet's whole API surface, hand-written rather than generated.
//
// Generating a client from df-api-v1 would pull the entire website API into a
// build whose only job is API access — the catalog, favourites, generations and
// everything else a developer has no page for here.

// The cabinet calls its own origin, so there is normally no prefix at all. A
// lone "/" is what the build pipeline passes when it means "none" — GitHub
// expressions cannot carry an empty string through a ternary without it
// falling through to the next branch.
const configured = import.meta.env.VITE_API_URL || '';
const BASE = configured === '/' ? '' : configured.replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method,
    // The session is the cookie dfapi already sets. No token in local storage:
    // anything a script on the page can read, a script on the page can send.
    credentials: 'include',
    headers: body === undefined ? headers : { 'Content-Type': 'application/json', ...headers },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // A session that expired mid-visit used to surface as "Could not load keys"
  // on whichever page happened to be open, with a working-looking cabinet
  // around it. One event, and the app returns to the sign-in form instead.
  if (response.status === 401 && !path.startsWith('/api/v1/auth/')) {
    window.dispatchEvent(new CustomEvent('api-unauthorized'));
  }

  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  const parsed = text ? JSON.parse(text) : {};
  if (!response.ok) {
    const message =
      parsed?.error?.message || parsed?.error?.code || parsed?.error || response.statusText;
    throw new ApiError(response.status, String(message));
  }
  return parsed as T;
}

export type Session = {
  authenticated: boolean;
  user?: { id: string; display_name?: string; email?: string };
};

export type Account = {
  id: number;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED';
  concurrent_limit: number;
  rps_limit: number;
  balance: number;
  created_at: string;
  // What this account can actually pay with. Crypto keys are read off a bot
  // record, so an account with no bot of its own is offered card only.
  topup_providers?: string[];
};

export type Key = {
  id: number;
  name: string;
  prefix: string;
  ip_allowlist?: string[];
  revoked: boolean;
  last_used_at?: string;
  created_at: string;
};

export type CreatedKey = { key: Key; plaintext: string };

export type UsageDay = {
  date: string;
  topped_up: number;
  spent: number;
  refunded: number;
};

export type Usage = {
  from: string;
  to: string;
  topped_up: number;
  spent: number;
  refunded: number;
  remaining: number;
  days?: UsageDay[];
};

export type Movement = {
  id: number;
  delta: number;
  reason: 'TOPUP' | 'GENERATION' | 'REFUND' | 'ADJUSTMENT';
  reference?: string;
  note?: string;
  created_at: string;
};

export type Webhook = {
  configured: boolean;
  url?: string;
  enabled: boolean;
  // Only on rotate and reveal, both behind an emailed code. A plain read of the
  // webhook used to carry it, so any session cookie was enough to forge
  // deliveries to the customer's endpoint.
  signing_secret?: string;
};

export type Delivery = {
  id: number;
  generation_id: number;
  status: 'PENDING' | 'DELIVERED' | 'FAILED';
  attempts: number;
  last_status?: number;
  last_error?: string;
  next_attempt_at?: string;
  delivered_at?: string;
  created_at: string;
};

export type Generation = {
  id: number;
  status: string;
  price_credits: number;
  template_id?: string;
  result_url?: string;
  error?: string;
  created_at: string;
};

export type TopUp = {
  payment_id: string;
  provider: string;
  amount_credits: number;
  status: string;
  checkout_url: string;
};

// A code is asked for by header, so every action carries it the same way
// whatever shape its own request has.
export type Confirmation = { code: string; token: string } | undefined;

const confirmHeaders = (confirm: Confirmation): Record<string, string> =>
  confirm ? { 'X-Confirmation-Code': confirm.code, 'X-Confirmation-Token': confirm.token } : {};

export const api = {
  session: () => call<Session>('GET', '/api/v1/auth/session'),
  requestEmailCode: (email: string) =>
    call<unknown>('POST', '/api/v1/auth/email/request', { email }),
  verifyEmailCode: (email: string, code: string) =>
    call<unknown>('POST', '/api/v1/auth/email/verify', { email, code }),
  // The link in the sign-in email carries the challenge itself, so it needs no
  // address and no code typed back.
  verifyEmailToken: (token: string) => call<unknown>('POST', '/api/v1/auth/email/verify', { token }),
  logout: () => call<unknown>('POST', '/api/v1/auth/logout'),

  account: () => call<Account>('GET', '/api/v1/me/api-access'),
  enable: () => call<Account>('POST', '/api/v1/me/api-access'),

  keys: () => call<{ items: Key[] }>('GET', '/api/v1/me/api-access/keys'),
  createKey: (name: string, allowList: string[], confirm?: Confirmation) =>
    call<CreatedKey>(
      'POST',
      '/api/v1/me/api-access/keys',
      { name: name || undefined, ip_allowlist: allowList.length ? allowList : undefined },
      confirmHeaders(confirm),
    ),
  revokeKey: (id: number, confirm?: Confirmation) =>
    call<void>('DELETE', `/api/v1/me/api-access/keys/${id}`, undefined, confirmHeaders(confirm)),

  usage: (windowDays: number) =>
    call<Usage>('GET', `/api/v1/me/api-access/usage?window_days=${windowDays}`),
  ledger: (windowDays: number) =>
    call<{ items: Movement[]; balance: number }>(
      'GET',
      `/api/v1/me/api-access/ledger?window_days=${windowDays}&limit=200`,
    ),
  topUp: (provider: string, amountCredits: number) =>
    call<TopUp>('POST', '/api/v1/me/api-access/topup', {
      provider,
      amount_credits: amountCredits,
    }),

  generations: (before?: number, status?: string) => {
    const params = new URLSearchParams({ limit: '50' });
    if (before) params.set('before', String(before));
    if (status) params.set('status', status);
    return call<{ items: Generation[]; next_before?: number }>(
      'GET',
      `/api/v1/me/api-access/generations?${params.toString()}`,
    );
  },

  webhook: () => call<Webhook>('GET', '/api/v1/me/api-access/webhook'),
  setWebhook: (url: string, enabled: boolean, confirm?: Confirmation) =>
    call<Webhook>('PUT', '/api/v1/me/api-access/webhook', { url, enabled }, confirmHeaders(confirm)),
  removeWebhook: () => call<void>('DELETE', '/api/v1/me/api-access/webhook'),
  rotateWebhookSecret: (confirm?: Confirmation) =>
    call<Webhook>('POST', '/api/v1/me/api-access/webhook/secret', undefined, confirmHeaders(confirm)),

  requestConfirmation: (action: string) =>
    call<{ confirmation_token: string; expires_in_seconds: number }>(
      'POST',
      '/api/v1/me/confirmations',
      { action },
    ),
  revealWebhookSecret: (confirm?: Confirmation) =>
    call<Webhook>(
      'POST',
      '/api/v1/me/api-access/webhook/secret/reveal',
      undefined,
      confirmHeaders(confirm),
    ),
  testWebhook: () =>
    call<{ delivered: boolean; status?: number; error?: string }>(
      'POST',
      '/api/v1/me/api-access/webhook/test',
    ),
  deliveries: () =>
    call<{ items: Delivery[] }>('GET', '/api/v1/me/api-access/webhook/deliveries?limit=50'),
};
